import { useEffect, useRef, useState } from "react";
import { CalendarDays } from "lucide-react";
import { it } from "date-fns/locale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const pad = (n) => String(n).padStart(2, "0");
const ISO = /^(\d{4})-(\d{2})-(\d{2})/;
const toText = (iso) => { const m = ISO.exec(iso || ""); return m ? `${m[3]}/${m[2]}/${m[1]}` : ""; };
const toDate = (iso) => { const m = ISO.exec(iso || ""); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : undefined; };
const COARSE = typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;
const parse = (txt, short = false) => {
  const s = txt.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const yr = short ? "(\\d{4}|\\d{2})" : "(\\d{4})";
  const m = new RegExp(`^(\\d{1,2})[/.\\-\\s](\\d{1,2})[/.\\-\\s]${yr}$`).exec(s) || new RegExp(`^(\\d{2})(\\d{2})${yr}$`).exec(s);
  if (!m) return null;
  const [d, mo, y] = [Number(m[1]), Number(m[2]), m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])];
  const dt = new Date(y, mo - 1, d);
  return dt.getMonth() === mo - 1 && dt.getDate() === d ? `${y}-${pad(mo)}-${pad(d)}` : null;
};

// Data sempre in formato italiano gg/mm/aaaa (scrivibile a mano o dal calendario); restituisce YYYY-MM-DD.
export const DateInput = ({ value, onChange, className = "", disabled, ...props }) => {
  const [text, setText] = useState(toText(value));
  const [open, setOpen] = useState(false);
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setText(toText(value)); }, [value]);
  const emit = (iso) => onChange?.({ target: { value: iso } });
  const onType = (e) => {
    const v = e.target.value;
    setText(v);
    const iso = parse(v);
    if (iso) emit(iso);
    else if (!v.trim()) emit("");
  };
  const onBlur = () => {
    focused.current = false;
    const iso = parse(text, true);
    if (iso && iso !== value) emit(iso);
    setText(toText(iso || value));
  };
  const pick = (d) => {
    if (!d) return;
    emit(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
    setOpen(false);
  };
  const heights = className.split(" ").filter((c) => c.startsWith("h-")).join(" ");
  const tid = props["data-testid"];
  // Su telefono/tablet: calendario nativo del sistema (sempre toccabile, niente «/» da digitare).
  if (COARSE) {
    return <Input type="date" value={(value || "").slice(0, 10)} onChange={(e) => emit(e.target.value)} disabled={disabled}
      className={cn("block w-full min-h-[44px] appearance-none text-base", className)} {...props} />;
  }
  return (
    <div className={cn("relative", className)}>
      <Input value={text} onChange={onType} onFocus={() => { focused.current = true; }} onBlur={onBlur} placeholder="gg/mm/aaaa" inputMode="numeric"
        disabled={disabled} className={cn("pr-9 w-full", heights)} {...props} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} aria-label="Apri calendario" data-testid={tid ? `${tid}-calendar` : undefined}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
            <CalendarDays className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar mode="single" locale={it} weekStartsOn={1} selected={toDate(value)} defaultMonth={toDate(value)} onSelect={pick} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DateInput;

const TIME = /^([01]?\d|2[0-3])[:.]?([0-5]\d)$/;

// Data + ora in formato italiano; restituisce "YYYY-MM-DDTHH:MM" come un campo datetime-local.
export const DateTimeInput = ({ value, onChange, className = "", ...props }) => {
  const [d, t = ""] = (value || "").split("T");
  const hm = t.slice(0, 5);
  const [time, setTime] = useState(hm);
  useEffect(() => { setTime(hm); }, [hm]);
  const emit = (dd, tt) => onChange?.({ target: { value: dd ? `${dd}T${tt || "08:00"}` : "" } });
  const onTime = (e) => {
    const v = e.target.value;
    setTime(v);
    const m = TIME.exec(v.trim());
    if (m && d) emit(d, `${m[1].padStart(2, "0")}:${m[2]}`);
  };
  const tid = props["data-testid"];
  return (
    <div className={cn("flex gap-2", className)}>
      <DateInput value={d || ""} onChange={(e) => emit(e.target.value, hm)} className="flex-1 min-w-0" data-testid={tid} />
      <Input value={time} onChange={onTime} onBlur={() => setTime(hm)} placeholder="hh:mm" inputMode="numeric" className="w-[72px] text-center"
        data-testid={tid ? `${tid}-time` : undefined} />
    </div>
  );
};
